/**
 * Owner card grids (pattern 15, TMMAPP-0096). Pointer, touch and keyboard sorting share one
 * reorder call per drop. A non-owner render is the same grid with no handle and no drag context.
 *
 * Drag feedback (TMMAPP-0120 item 20): on pick-up the card lifts into the overlay
 * (`elevation.dialog`), which is portaled to `document.body` so no ancestor's transform, filter,
 * `contain` or clipped overflow can misplace it, and follows the pointer; a dashed placeholder marks
 * the landing spot; the other cards slide aside (200 ms) and the card settles into the placeholder on
 * drop (200 ms). Reduced motion removes the animation, never the feedback: the overlay still follows
 * the pointer and the other cards still make room, instantly, and the drop does not animate.
 */
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  type DropAnimation,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { Box, IconButton, useMediaQuery, type SxProps, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { labelCanvasSx } from '@/theme/textLabel';
import { resolveTokens } from '@/theme/theme';
import { cardGridSx } from './cardGrid';
import { DragHandleIcon } from './icons';

const POINTER_OPTIONS = { activationConstraint: { distance: 8 } };
const TOUCH_OPTIONS = { activationConstraint: { delay: 200, tolerance: 8 } };
const KEYBOARD_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };
/** The overlay's wrapper, under `document.body` while a card is dragged. */
const DRAG_OVERLAY_CLASS = 'tmm-drag-overlay';
/** The neighbours' slide and the drop's settle. */
const MOTION_MS = 200;
const SORTABLE_TRANSITION = { duration: MOTION_MS, easing: 'ease' };
const DROP_ANIMATION: DropAnimation = {
  duration: MOTION_MS,
  easing: 'ease',
  // The card's own cell stays hidden until the overlay has settled into it, so it lands once.
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { visibility: 'hidden' } } }),
};

export interface ReorderHandleProps {
  id: string | number;
  name: string;
  canMoveEarlier: boolean;
  canMoveLater: boolean;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  instructionsId: string;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
}

interface SortableCardGridProps<T> {
  items: readonly T[];
  getId: (item: T) => string | number;
  /** Accessible name for the handle and the live announcements. */
  getName: (item: T) => string;
  renderCard: (item: T, handle: ReorderHandleProps | null) => ReactNode;
  onReorder: (ids: Array<string | number>) => void | Promise<void>;
  canReorder: boolean;
  /** Rendered first, outside the sortable set, with no handle. */
  pinnedFirstId?: string | number;
  /**
   * Items outside the reorder set (a granted or a closed account, a disposed holding): rendered
   * after the sortable ones, with no handle. Every item is sortable when absent.
   */
  isSortable?: (item: T) => boolean;
  /** First, and never sortable: a card that is not one of the items (the Unassigned card, TMMAPP-0109). */
  leading?: ReactNode;
  /** Last, and never sortable. The add card. */
  trailing?: ReactNode;
  sx?: SxProps<Theme>;
  /** The cards' minimum width: 272 (envelope cards, the default) or 258 (account cards). */
  minCardWidth?: number;
}

/**
 * Six-dot grab. The grid passes the dnd-kit activator wiring through `renderCard`. `corner` sits
 * the handle in the card's top inline-start corner, in its padding, so it never moves the name
 * (TMMAPP-0097 §D 30): a 24px target with its focus ring drawn inside, which the card's
 * `overflow: hidden` cannot clip. The card is the positioning context.
 *
 * `corner="end"` (account and virtual account cards, TMMAPP-0154 item 10) adds no row: the handle
 * floats in the card's top padding at the inline-end, beside the kebab, and shows while the card is
 * hovered or holds focus (the card spreads `REVEAL_REORDER_HANDLE_SX`), when it has focus itself,
 * and always on a touch screen. It stays in the tab order; the kebab's Move earlier / Move later stay
 * the keyboard's other way.
 */
export function ReorderHandle({
  id,
  name,
  instructionsId,
  setActivatorNodeRef,
  attributes,
  listeners,
  corner = false,
}: ReorderHandleProps & { corner?: boolean | 'end' }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <IconButton
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      type="button"
      data-reorder-handle=""
      data-reorder-id={String(id)}
      aria-label={t('reorder.handle', { name })}
      aria-describedby={instructionsId}
      sx={{
        color: tokens.text2,
        cursor: 'grab',
        minWidth: 24,
        minHeight: 24,
        width: 40,
        height: 40,
        flexShrink: 0,
        touchAction: 'none',
        '&[aria-pressed="true"], &:active': { cursor: 'grabbing' },
        '&:focus-visible': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: corner ? -2 : 2,
        },
        ...(corner === 'end'
          ? {
              position: 'absolute',
              // In the card's top padding, inline-start of the 28 px kebab and its 12 px inset:
              // the 16 px glyph clears the name row.
              top: 0,
              insetInlineEnd: 44,
              zIndex: 2,
              width: 24,
              height: 24,
              p: 0,
              borderRadius: `${tokens.radius.control}px`,
              // Fully hidden at rest and fully shown on reveal, never a partial fade: it stays
              // focusable, so focus shows it (`noContentFades` allowlists this reveal).
              opacity: 0,
              '&:focus-visible, &[aria-pressed="true"]': { opacity: 1 },
              '@media (hover: none)': { opacity: 1 },
            }
          : {}),
        ...(corner === true
          ? {
              position: 'absolute',
              top: 0,
              insetInlineStart: 0,
              zIndex: 1,
              width: 24,
              height: 24,
              p: 0,
              borderRadius: `${tokens.radius.control}px`,
            }
          : {}),
      }}
    >
      <DragHandleIcon size={corner ? 16 : 20} />
    </IconButton>
  );
}

export function SortableCardGrid<T>(props: SortableCardGridProps<T>) {
  if (!props.canReorder) return <StaticCardGrid {...props} />;
  return <ReorderableCardGrid {...props} />;
}

function splitPinned<T>(
  items: readonly T[],
  getId: (item: T) => string | number,
  pinnedFirstId: string | number | undefined,
  isSortable: ((item: T) => boolean) | undefined,
): { pinned: T | undefined; sortable: T[]; fixed: T[] } {
  const pinnedKey = pinnedFirstId === undefined ? undefined : String(pinnedFirstId);
  const rest = items.filter((item) => String(getId(item)) !== pinnedKey);
  return {
    pinned:
      pinnedKey === undefined ? undefined : items.find((item) => String(getId(item)) === pinnedKey),
    sortable: isSortable === undefined ? rest : rest.filter((item) => isSortable(item)),
    fixed: isSortable === undefined ? [] : rest.filter((item) => !isSortable(item)),
  };
}

function sameOrder(
  left: readonly (string | number)[],
  right: readonly (string | number)[],
): boolean {
  return (
    left.length === right.length && left.every((id, index) => String(id) === String(right[index]))
  );
}

function translateOf(transform: { x: number; y: number } | null): string | undefined {
  if (transform === null) return undefined;
  return `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`;
}

function PlainCell({ children }: { children: ReactNode }) {
  return <Box sx={{ minWidth: 0, height: '100%' }}>{children}</Box>;
}

function TrailingCell({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minWidth: 0, minHeight: '100%', '& > *': { flex: 1 } }}>
      {children}
    </Box>
  );
}

function StaticCardGrid<T>({
  items,
  getId,
  renderCard,
  pinnedFirstId,
  isSortable,
  leading,
  trailing,
  sx,
  minCardWidth,
}: SortableCardGridProps<T>) {
  const { pinned, sortable, fixed } = splitPinned(items, getId, pinnedFirstId, isSortable);
  return (
    <Box sx={cardGridSx(sx, minCardWidth)}>
      {leading !== undefined && <PlainCell>{leading}</PlainCell>}
      {pinned !== undefined && <PlainCell>{renderCard(pinned, null)}</PlainCell>}
      {[...sortable, ...fixed].map((item) => (
        <PlainCell key={String(getId(item))}>{renderCard(item, null)}</PlainCell>
      ))}
      {trailing !== undefined && <TrailingCell>{trailing}</TrailingCell>}
    </Box>
  );
}

function ReorderableCardGrid<T>({
  items,
  getId,
  getName,
  renderCard,
  onReorder,
  pinnedFirstId,
  isSortable,
  leading,
  trailing,
  sx,
  minCardWidth,
}: SortableCardGridProps<T>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const instructionsId = useId();
  const instructions = t('reorder.instructions');
  const sensors = useSensors(
    useSensor(MouseSensor, POINTER_OPTIONS),
    useSensor(TouchSensor, TOUCH_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_OPTIONS),
  );
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const handles = useRef(new Map<string, HTMLElement>());
  const itemsRef = useRef(items);
  const getIdRef = useRef(getId);
  const getNameRef = useRef(getName);
  const pinnedRef = useRef(pinnedFirstId);
  const isSortableRef = useRef(isSortable);
  const leadingRef = useRef(leading !== undefined);
  useLayoutEffect(() => {
    itemsRef.current = items;
    getIdRef.current = getId;
    getNameRef.current = getName;
    pinnedRef.current = pinnedFirstId;
    isSortableRef.current = isSortable;
    leadingRef.current = leading !== undefined;
  });

  const { pinned, sortable, fixed } = splitPinned(items, getId, pinnedFirstId, isSortable);
  const sortableIds = sortable.map((item) => getId(item));

  const nameOf = (id: UniqueIdentifier) => {
    const item = itemsRef.current.find((entry) => String(getIdRef.current(entry)) === String(id));
    return item === undefined ? String(id) : getNameRef.current(item);
  };

  const landing = (overId: UniqueIdentifier) => {
    const idOf = getIdRef.current;
    const pinnedId = pinnedRef.current;
    const sortableOf = isSortableRef.current;
    const list =
      sortableOf === undefined
        ? itemsRef.current
        : itemsRef.current.filter(
            (item) => String(idOf(item)) === String(pinnedId) || sortableOf(item),
          );
    const hasPinned =
      pinnedId !== undefined && list.some((item) => String(idOf(item)) === String(pinnedId));
    const rows = hasPinned
      ? list.filter((item) => String(idOf(item)) !== String(pinnedId))
      : [...list];
    const overIndex = rows.findIndex((item) => String(idOf(item)) === String(overId));
    if (overIndex < 0) return undefined;
    // The announced position counts the fixed cards in front (a pinned item, a leading card).
    const offset = (hasPinned ? 1 : 0) + (leadingRef.current ? 1 : 0);
    return { position: overIndex + 1 + offset, total: rows.length + offset };
  };

  const focusMoved = (id: UniqueIdentifier) => {
    const key = String(id);
    // Rollback can replace the activator node. The id distinguishes two cards that share a name.
    const fromMap = handles.current.get(key);
    const fromDom = document.querySelector<HTMLElement>(
      `[data-reorder-handle][data-reorder-id="${CSS.escape(key)}"]`,
    );
    const node = fromMap?.isConnected ? fromMap : fromDom?.isConnected ? fromDom : undefined;
    node?.focus();
  };

  const commit = (nextIds: Array<string | number>, focusId: UniqueIdentifier) => {
    if (sameOrder(sortableIds, nextIds)) return;
    // The parent rolls the list back and notifies, then rejects. Focus follows that paint.
    void Promise.resolve(onReorder(nextIds)).catch(() => {
      // Once on rejection, then after the rollback paint in case that render replaced the node.
      focusMoved(focusId);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusMoved(focusId);
        });
      });
    });
  };

  const indexOf = (id: UniqueIdentifier) =>
    sortable.findIndex((item) => String(getId(item)) === String(id));

  const moveBy = (id: UniqueIdentifier, delta: -1 | 1) => {
    const from = indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= sortable.length) return;
    commit(
      arrayMove(sortable, from, to).map((item) => getId(item)),
      id,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const from = indexOf(active.id);
    const to = indexOf(over.id);
    if (from < 0 || to < 0 || from === to) return;
    commit(
      arrayMove(sortable, from, to).map((item) => getId(item)),
      active.id,
    );
  };

  const activeItem =
    activeId === null
      ? undefined
      : sortable.find((item) => String(getId(item)) === String(activeId));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{
        screenReaderInstructions: { draggable: instructions },
        announcements: {
          onDragStart({ active }) {
            return t('reorder.pickedUp', { name: nameOf(active.id) });
          },
          onDragOver({ active, over }) {
            if (!over || String(over.id) === String(active.id)) return undefined;
            const spot = landing(over.id);
            if (spot === undefined) return undefined;
            return t('reorder.moved', {
              name: nameOf(active.id),
              position: spot.position,
              total: spot.total,
            });
          },
          onDragEnd({ active }) {
            return t('reorder.dropped', { name: nameOf(active.id) });
          },
          onDragCancel() {
            return t('reorder.cancelled');
          },
        },
      }}
      onDragStart={(event: DragStartEvent) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <Box id={instructionsId} sx={visuallyHidden}>
        {instructions}
      </Box>
      <Box sx={cardGridSx(sx, minCardWidth)}>
        {leading !== undefined && <PlainCell>{leading}</PlainCell>}
        {pinned !== undefined && <PlainCell>{renderCard(pinned, null)}</PlainCell>}
        <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
          {sortable.map((item, index) => {
            const id = getId(item);
            return (
              <SortableCell
                key={String(id)}
                id={id}
                index={index}
                count={sortable.length}
                name={getName(item)}
                instructionsId={instructionsId}
                reduceMotion={reduceMotion}
                registerHandle={(element) => {
                  const key = String(id);
                  if (element) handles.current.set(key, element);
                  else handles.current.delete(key);
                }}
                onMoveEarlier={() => moveBy(id, -1)}
                onMoveLater={() => moveBy(id, 1)}
              >
                {(handle) => renderCard(item, handle)}
              </SortableCell>
            );
          })}
        </SortableContext>
        {fixed.map((item) => (
          <PlainCell key={String(getId(item))}>{renderCard(item, null)}</PlainCell>
        ))}
        {trailing !== undefined && <TrailingCell>{trailing}</TrailingCell>}
      </Box>
      {createPortal(
        <DragOverlay
          dropAnimation={reduceMotion ? null : DROP_ANIMATION}
          // The wrapper dnd-kit positions (fixed, following the pointer), a direct child of body.
          className={DRAG_OVERLAY_CLASS}
          // Above MUI's modal layer, so a card lifted inside a dialog stays on top.
          zIndex={theme.zIndex.modal + 1}
        >
          {activeItem !== undefined && (
            <Box
              aria-hidden
              sx={{
                boxShadow: tokens.elevation.dialog,
                borderRadius: `${tokens.radius.card}px`,
                bgcolor: tokens.surface,
                ...labelCanvasSx(tokens, 'surface'),
                opacity: 1,
              }}
            >
              {renderCard(activeItem, null)}
            </Box>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function SortableCell({
  id,
  index,
  count,
  name,
  instructionsId,
  reduceMotion,
  registerHandle,
  onMoveEarlier,
  onMoveLater,
  children,
}: {
  id: UniqueIdentifier;
  index: number;
  count: number;
  name: string;
  instructionsId: string;
  reduceMotion: boolean;
  registerHandle: (element: HTMLElement | null) => void;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  children: (handle: ReorderHandleProps) => ReactNode;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  // dnd-kit's default role description ("sortable") is spoken English in every locale.
  const sortableAttributes = { roleDescription: t('reorder.roleDescription') };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable(
    reduceMotion
      ? {
          id,
          attributes: sortableAttributes,
          transition: null,
          animateLayoutChanges: () => false,
        }
      : { id, attributes: sortableAttributes, transition: SORTABLE_TRANSITION },
  );
  const handle: ReorderHandleProps = {
    id,
    name,
    canMoveEarlier: index > 0,
    canMoveLater: index < count - 1,
    onMoveEarlier,
    onMoveLater,
    instructionsId,
    attributes,
    listeners,
    setActivatorNodeRef: (element) => {
      registerHandle(element);
      setActivatorNodeRef(element);
    },
  };
  return (
    <Box
      ref={setNodeRef}
      data-sortable-id={String(id)}
      data-sortable-index={index}
      style={{
        transform: translateOf(transform),
        transition: reduceMotion ? undefined : transition,
      }}
      sx={{
        minWidth: 0,
        height: '100%',
        borderRadius: `${tokens.radius.card}px`,
        ...(isDragging
          ? { border: `1px dashed ${tokens.lineStrong}`, bgcolor: tokens.surface2 }
          : {}),
      }}
    >
      <Box
        sx={{
          height: '100%',
          ...(isDragging
            ? { visibility: 'hidden', '& [data-reorder-handle]': { visibility: 'visible' } }
            : {}),
        }}
      >
        {children(handle)}
      </Box>
    </Box>
  );
}
